import { useEffect, useState } from 'react'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import MenuRoundedIcon from '@mui/icons-material/MenuRounded'
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded'
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded'
import type { Channel } from '../../../types/chat'
import { useNls } from '../../../nls/nlsProvider'
import { useWorkspace } from '../../../context/useWorkspace'
import { IconButton } from '../../Common/IconButton'
import { MemberNexusHero, type MemberNexusChip } from '../../Member/controls/MemberNexusPrimitives'
import { isWhatsappChannel, maskWhatsappNumber, replyWindow } from '../../../utils/whatsappChat'

type Props = {
  channel: Channel | null
  onlineCount: number
  memberCount: number
  extraChips?: MemberNexusChip[]
  helperMode?: boolean
  onOpenChannels: () => void
  onOpenMembers: () => void
  onOpenAi: () => void
  onOpenMore: () => void
}

export const MemberChatHeader = ({
  channel,
  onlineCount,
  memberCount,
  extraChips = [],
  helperMode = false,
  onOpenChannels,
  onOpenMembers,
  onOpenAi,
  onOpenMore,
}: Props) => {
  const { t } = useNls()
  const { workspaces = [], activeWorkspaceId } = useWorkspace()
  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId)
  const isWhatsapp = isWhatsappChannel(channel)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!isWhatsapp) return
    const timer = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(timer)
  }, [isWhatsapp])

  const currentWorkspace = activeWorkspace ?? workspaces.find((w) => w.id === activeWorkspaceId)
  const route = currentWorkspace?.settings?.whatsapp?.route === 'OWN' ? 'OWN' : 'TIMELINING'
  const windowInfo = replyWindow(channel?.whatsappLastInboundAt, now)
  const maskedPhone = channel?.whatsappE164 ? maskWhatsappNumber(channel.whatsappE164) : ''

  const title = channel?.name ?? 'Channels'

  const whatsappChips: MemberNexusChip[] = isWhatsapp
    ? [
        {
          key: 'wa-route',
          label: route === 'OWN' ? t('NLSChatRouteOwn') : t('NLSChatRouteTimelining'),
          tone: 'neutral',
        },
        {
          key: 'wa-window',
          label: windowInfo.open
            ? t('NLSChatReplyWindowLeft', { hours: Math.max(1, Math.ceil(windowInfo.msLeft / (60 * 60 * 1000))) })
            : t('NLSChatReplyWindowClosed'),
          tone: windowInfo.open ? 'live' : 'neutral',
        },
        {
          key: 'wa-analysis',
          label: t('NLSChatAnalysisOff'),
          tone: 'neutral',
        },
      ]
    : []

  const chips: MemberNexusChip[] = [
    { key: 'switch', label: 'Switch', icon: <MenuRoundedIcon fontSize="inherit" />, onClick: onOpenChannels },
    ...(helperMode ? [] : [{
      key: 'online',
      label: onlineCount > 0 ? 'Online' : t('NLSChatLabelMembers'),
      value: onlineCount > 0 ? onlineCount : memberCount,
      icon: <PeopleAltRoundedIcon fontSize="inherit" />,
      onClick: onOpenMembers,
      tone: onlineCount > 0 ? 'live' as const : 'neutral' as const,
    }]),
    ...whatsappChips,
    ...extraChips,
    { key: 'ai', label: 'AI', icon: <AutoAwesomeRoundedIcon fontSize="inherit" />, onClick: onOpenAi },
    { key: 'more', label: 'More', icon: <MoreVertRoundedIcon fontSize="inherit" />, onClick: onOpenMore },
  ]

  if (helperMode) {
    return (
      <div
        className="flex items-center gap-2 border-b border-[var(--border)] bg-[var(--surface)] px-3 py-2"
        role="banner"
        data-testid="helper-chat-header"
      >
        <IconButton
          size="md"
          variant="tertiary"
          onClick={onOpenChannels}
          label="Open channels"
          icon={<MenuRoundedIcon style={{ fontSize: '20px' }} />}
        />
        <div className="min-w-0 flex-1 truncate">
          <p className="truncate text-sm font-bold uppercase tracking-[0.14em] text-[var(--text-strong)]">{title}</p>
          {isWhatsapp && maskedPhone ? (
            <p className="truncate text-xs text-[var(--text-muted)]" data-testid="whatsapp-header-mask">{maskedPhone}</p>
          ) : null}
        </div>
        <IconButton
          size="md"
          variant="tertiary"
          onClick={onOpenAi}
          label="Open AI"
          icon={<AutoAwesomeRoundedIcon style={{ fontSize: '20px' }} />}
        />
        <IconButton
          size="md"
          variant="tertiary"
          onClick={onOpenMore}
          label="More options"
          icon={<MoreVertRoundedIcon style={{ fontSize: '20px' }} />}
        />
      </div>
    )
  }

  return (
    <div className="memberChatHeader" role="banner">
      <MemberNexusHero eyebrow={isWhatsapp && maskedPhone ? maskedPhone : 'Chat'} title={title} chips={chips} />
    </div>
  )
}
